import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { useSubRoutes, useExactRedirect, usePermissionTabs } from 'hooks';
import { globalPropTypes, checkPermissions, FEATURE_MA_YC_APPLY } from 'helpers';

import { Header, Tabs, Alert, Container, PageContent } from 'components';
import { loadIpmaPeopleBarCreator } from 'store/actionsCreators';
import { useTranslation } from 'react-i18next';

const breadcrumbs = [
  {
    title: 'navigation.breadcrumbs.ipma.people',
    url: '/ipma/people',
  },
];

const initialTabs = [
  {
    title: 'navigation.tabs.ipma.people.myIpma',
    path: '/ipma/people/users',
    key: 'users',
  },
  // What the membership automations have produced, per association. Held back with the rest of
  // the apply feature: nothing has been issued while it is off, so the tables would read as rows
  // of zeroes and invite the wrong conclusion.
  {
    title: 'navigation.tabs.ipma.people.maMembershipStatistics',
    path: '/ipma/people/ma-membership-statistics',
    key: 'maMembershipStatistics',
  },
  {
    title: 'navigation.tabs.ipma.people.ycMembershipStatistics',
    path: '/ipma/people/yc-membership-statistics',
    key: 'ycMembershipStatistics',
  },
];

const People = ({ routes, loadBar, bar, userPermissions }) => {
  const { t } = useTranslation();
  useEffect(() => {
    loadBar();
  }, []);

  const visibleTabs = FEATURE_MA_YC_APPLY
    ? initialTabs
    : initialTabs.filter((tab) => tab.key === 'users');

  const [renderSubRoutes] = useSubRoutes(routes);
  const [tabs, firstTabPath] = usePermissionTabs(visibleTabs, routes, userPermissions, bar);
  const [renderExactRedirect] = useExactRedirect('/ipma/people', firstTabPath);

  // Every tab shows a count beside its name, which reads as a queue waiting to be worked through.
  // The statistics tabs are reports, so they carry no number: usePermissionTabs defaults one to
  // zero, and an undefined count is what the tab bar renders as no badge at all.
  const tabItems = tabs.map((tab) => (tab.key === 'users' ? tab : { ...tab, count: undefined }));

  return (
    <>
      <Header
        breadcrumbs={breadcrumbs}
        title={t('page.ipma.people.title')}
        renderActions={({ Button: ButtonRender }) =>
          checkPermissions(
            {
              roles: ['ipma_manage_ipma_user_list'],
            },
            userPermissions,
          ) ? (
            <ButtonRender secondary as={Link} to="/ipma/people/add">
              {t('page.ipma.people.addIpmaUserButton')}
            </ButtonRender>
          ) : null
        }
      />
      <Tabs items={tabItems} />
      {checkPermissions(
        {
          roles: ['ipma_manage_ipma_user_list'],
        },
        userPermissions,
      ) ? (
        <>
          {renderExactRedirect()}
          {renderSubRoutes()}
        </>
      ) : (
        <Container>
          <PageContent>
            <Alert type="info">{t('page.forbidden.subtitle')}</Alert>
          </PageContent>
        </Container>
      )}
    </>
  );
};

People.propTypes = {
  routes: PropTypes.arrayOf(globalPropTypes.RoutePropTypes),
  loadBar: PropTypes.func.isRequired,
  bar: PropTypes.shape({
    users: PropTypes.number,
    organisations: PropTypes.number,
    list: PropTypes.number,
  }).isRequired,
  userPermissions: globalPropTypes.UserPermissions.isRequired,
};

People.defaultProps = {
  routes: [],
};

const mapStateToProps = ({ ipma, user }) => ({
  bar: ipma.people.bar,
  userPermissions: user.userPermissions,
});

const mapDispatchToProps = (dispatch) => ({
  loadBar: () => dispatch(loadIpmaPeopleBarCreator()),
});

export default connect(mapStateToProps, mapDispatchToProps)(People);
